import type * as React from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';

import type { Block } from '@gitbook/slate';
import type { EditorContainer } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import {
    Description,
    Dialog,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    FieldError,
    Form,
    Heading,
    Label,
    Submit,
    TextField,
    TextInput,
    useDialogClose,
} from '@gitbook/spine-react';
import { normalizeURLAnchor } from '@gitbook/util-url';

/**
 * Dialog to edit the ID of the heading.
 */
export function EditHeadingDialog(props: {
    node: Block;
    editor: EditorContainer;
}): React.ReactElement {
    return (
        <Dialog size="auto">
            <DialogHeader>
                <Heading>Edit heading anchor</Heading>
            </DialogHeader>
            <EditHeadingForm {...props} />
        </Dialog>
    );
}

type EditHeadingFormInputs = {
    id: string;
};

function EditHeadingForm(props: { node: Block; editor: EditorContainer }): React.ReactElement {
    const { node, editor } = props;
    const close = useDialogClose();

    const defaultID = normalizeURLAnchor(node.text);

    const form = useForm<EditHeadingFormInputs>({
        values: {
            id: node.data.get('id') || '',
        },
    });

    /**
     * Normalize the value when typing.
     * But don't remove characters until the user hits the enter key.
     * Basically it makes it possible to type a space or dash at the end.
     */
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        const normalizedValue = normalizeURLAnchor(newValue);
        if (normalizedValue.length === newValue.length) {
            form.setValue('id', normalizedValue);
        } else {
            form.setValue('id', newValue);
        }
    };

    const onSubmit: SubmitHandler<EditHeadingFormInputs> = async ({ id }) => {
        editor.change((change) => {
            if (id) {
                change.setDataByKey(node.key, ['id'], normalizeURLAnchor(id));
            } else {
                change.removeDataByKey(node.key, ['id']);
            }
        });
        close();
    };

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <DialogBody>
                <TextField name="id" minLength={0} maxLength={100}>
                    <Label>Anchor</Label>
                    <Description>
                        Edit this anchor to customize a nice looking url for this heading.
                    </Description>
                    <TextInput
                        leadingIcon={Icon.Hash}
                        autoFocus
                        placeholder={defaultID}
                        onChange={handleInputChange}
                    />
                    <FieldError />
                </TextField>
            </DialogBody>
            <DialogFooter>
                <DialogCloseButton />
                <Submit label="Save" />
            </DialogFooter>
        </Form>
    );
}
